import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Compass, Anchor, Shield, Heart } from "lucide-react";
import { useLang } from "../context/LanguageContext";

const icons = [Compass, Anchor, Shield, Heart];

const About = () => {
  const { t, lang } = useLang();
  return (
    <div data-testid="about-page" className="pt-28 md:pt-36">
      {/* Hero */}
      <section className="container-x pb-16 md:pb-24">
        <p className="label-eyebrow">{t.about.eyebrow}</p>
        <h1 className="h-display mt-6 max-w-4xl text-5xl md:text-7xl font-light text-[#0B192C]">
          {t.about.title}
        </h1>
      </section>

      {/* Image + story */}
      <section className="container-x grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="lg:col-span-7 relative overflow-hidden rounded-2xl aspect-[4/3]"
        >
          <img
            src="https://images.pexels.com/photos/33628180/pexels-photo-33628180.jpeg"
            alt="Lofoten"
            className="h-full w-full object-cover"
          />
        </motion.div>
        <div className="lg:col-span-5 lg:pt-6 space-y-6">
          {t.about.paragraphs.map((p, i) => (
            <p key={i} className="text-[17px] leading-relaxed text-[#475569] font-light">
              {p}
            </p>
          ))}
        </div>
      </section>

      {/* Boat section */}
      <section className="section-pad">
        <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5 lg:order-2 relative overflow-hidden rounded-2xl aspect-[4/5]">
            <img
              src="https://images.unsplash.com/photo-1612764324168-7a3a318e0cbb"
              alt="Viksund 340 St. Cruz"
              className="h-full w-full object-cover"
            />
          </div>
          <div className="lg:col-span-7 lg:order-1 flex flex-col justify-center">
            <p className="label-eyebrow">Viksund 340 St. Cruz</p>
            <h2 className="h-display mt-4 text-3xl md:text-5xl font-light text-[#0B192C]">
              {t.about.boat_title}
            </h2>
            <p className="mt-6 text-[17px] leading-relaxed text-[#475569] font-light">{t.about.boat_body}</p>
            <ul className="mt-8 grid grid-cols-2 gap-4 text-sm text-[#0B192C]">
              {[
                { k: "10.4m", v: t.extras.stat_length },
                { k: "10", v: t.extras.stat_max_guests },
                { k: "4", v: t.extras.stat_berths },
                { k: "365", v: t.extras.stat_days_year },
              ].map((x, i) => (
                <li key={i} className="border-t border-black/10 pt-4">
                  <p className="h-display text-3xl font-light">{x.k}</p>
                  <p className="mt-1 text-xs uppercase tracking-[0.22em] text-[#94A3B8]">{x.v}</p>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="section-pad bg-[#F7F5F0]">
        <div className="container-x">
          <div className="max-w-xl">
            <p className="label-eyebrow">{t.extras.about_values_eyebrow}</p>
            <h2 className="h-display mt-4 text-3xl md:text-5xl font-light text-[#0B192C]">
              {t.extras.about_values_title}
            </h2>
          </div>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {t.about.values.map((v, i) => {
              const Icon = icons[i % icons.length];
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.08 }}
                  className="card-soft p-8"
                  data-testid={`about-value-${i}`}
                >
                  <span className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-[#0B192C] text-white">
                    <Icon size={18} strokeWidth={1.6} />
                  </span>
                  <h3 className="h-display mt-6 text-xl text-[#0B192C]">{v.title}</h3>
                  <p className="mt-3 text-[#475569] font-light leading-relaxed">{v.body}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-[#0B192C] text-white">
        <div className="container-x py-20 md:py-28 flex flex-col md:flex-row md:items-center md:justify-between gap-8">
          <h3 className="h-display text-3xl md:text-5xl font-light max-w-2xl">
            {t.extras.about_cta_title}
          </h3>
          <Link to="/booking" className="btn-primary bg-white text-[#0B192C] hover:bg-[#E5D9C5]" data-testid="about-cta-book">
            {t.nav.book_now} <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default About;
